import { Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

/**
 * iter187 — "Why am I being fined?" auto-explainer popover.
 *
 * Renders a small (i) icon next to a ledger row's note. Tap/click opens a
 * tooltip-style card with a human one-line summary + a structured list of
 * the underlying timestamps and reasons. All data comes from the backend's
 * `explainer` field on each fine/session_fee ledger entry — no fetch needed
 * on click. Designed to remove the #1 cause of admin↔member arguments by
 * showing exactly WHY a fine was issued, sourced from real audit timestamps.
 */
export default function FineExplainerPopover({ explainer, testId }) {
  if (!explainer) return null;
  const { summary, details = [], fine_type } = explainer;

  // Color the icon to match the fine type (red for fines, amber for fees)
  const isFee = fine_type === 'session_fee';
  const iconClass = isFee ? 'text-amber-500' : 'text-red-500';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`shrink-0 rounded-full hover:bg-muted active:scale-95 transition p-0.5 ${iconClass}`}
          data-testid={testId}
          aria-label="Why am I being fined?"
        >
          <Info className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        sideOffset={6}
        className="w-80 p-0 rounded-2xl border-2 shadow-xl"
        data-testid={`${testId}-content`}
      >
        <div className="p-4 space-y-3">
          {/* Headline */}
          <div className="flex items-start gap-2">
            <Info className={`w-4 h-4 mt-0.5 shrink-0 ${iconClass}`} />
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                {isFee ? 'Why this fee?' : 'Why this fine?'}
              </p>
              <p className="text-sm font-medium mt-1 leading-snug">{summary}</p>
            </div>
          </div>

          {/* Detail rows */}
          {details.length > 0 && (
            <div className="border-t pt-3 space-y-1.5">
              {details.map((d, i) => (
                <div key={i} className="flex items-baseline justify-between gap-3 text-xs">
                  <span className="text-muted-foreground shrink-0">{d.label}</span>
                  <span className="text-right font-medium" data-testid={`explainer-detail-${d.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    {d.value}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Footer hint */}
          <p className="text-xs text-muted-foreground border-t pt-2 italic">
            Pulled live from the audit log. If something looks wrong, ask an admin to reverse it.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
